import {Injectable, Signal} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {toSignal} from '@angular/core/rxjs-interop';
import {OAuthService} from 'angular-oauth2-oidc';
import type {CsMembership, CsUserinfo} from '@castlabs/ui';
import {from, Observable, switchMap} from 'rxjs';
import {filter, map, shareReplay, startWith} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {Organization} from '../types-repository';
import {ApiTokenService} from './api-token.service';

/** The janus service URN of the castlabs Content Platform license. */
export const CONTENT_PLATFORM_SERVICE = 'urn:janus:service:76b6e8558263431d9d766779987d6410';

/** Members of this organization are content platform staff. */
const STAFF_ORGANIZATION = 'castlabs_cpteam';

/** An organization as the UI shows it: the repository's Organization plus janus' parent name. */
export interface CpOrganization extends Organization {
  /** janus parent_display_name: shown as "via <parent>" under the organization name. Null for a top-level org. */
  parent_name: string | null;
}

/**
 * Who the user is and which organizations they belong to, read straight from janus
 * (`/userinfo`), as the Video Toolkit does.
 *
 * Every user holds a CS token by now -- castlabs users log in with one, above users get one from
 * the federate exchange (ApiTokenService) -- so janus answers for both, and nothing here needs to
 * know which IdP the user came from. Policies stay on the repository API, which fills in the
 * placeholders (bucket names, org tokens) that only the backend can resolve.
 */
@Injectable({providedIn: 'root'})
export class JanusService {

  readonly userinfo$: Observable<CsUserinfo>;

  /** Organizations holding a content platform license, by name. Undefined until janus answered. */
  readonly organizations: Signal<CpOrganization[] | undefined>;
  readonly name: Signal<string | undefined>;
  readonly email: Signal<string | undefined>;
  readonly staff: Signal<boolean>;

  constructor(oauthService: OAuthService, apiToken: ApiTokenService, http: HttpClient) {
    this.userinfo$ = oauthService.events.pipe(
      startWith(undefined),  // check once right away, not only on the next OAuth event
      filter(() => oauthService.hasValidAccessToken()),
      switchMap(() => from(apiToken.accessToken())),
      filter(token => !!token),
      switchMap(token => http.get<CsUserinfo>(`${environment.castlabs_idp_api_base}/userinfo`, {
        headers: new HttpHeaders({Authorization: `Bearer ${token}`}),
      })),
      shareReplay(1),
    );

    this.organizations = toSignal(this.userinfo$.pipe(map(JanusService.organizationsOf)));
    this.name = toSignal(this.userinfo$.pipe(map(u => u.profile.name || undefined)));
    this.email = toSignal(this.userinfo$.pipe(map(u => u.profile.email)));
    this.staff = toSignal(this.userinfo$.pipe(map(u =>
      u.memberships.some(m => m.organization.legacy_name === STAFF_ORGANIZATION))), {initialValue: false});
  }

  /**
   * The licensed organizations of a userinfo, by name.
   *
   * The same rule as the authorizer's extract_organizations_with_license: the organization itself
   * holds the license. Deliberately no filter on the license's status or expiry -- paying customers
   * carry status 0 with no expiry and the vocabulary is undocumented.
   */
  static organizationsOf(userinfo: CsUserinfo): CpOrganization[] {
    return userinfo.memberships
      .filter(m => m.organization.licenses.some(l => l.service === CONTENT_PLATFORM_SERVICE))
      .map(JanusService.organizationOf)
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  static organizationOf(membership: CsMembership): CpOrganization {
    const org = membership.organization;
    return {
      id: org.organization_urn,
      name: org.display_name,
      legacy_name: org.legacy_name!,  // always set on /userinfo organizations (the authorizer relies on it too)
      parent_name: org.parent_display_name,
    };
  }

  /** The janus service URNs licensed to one organization of the user. */
  static licensedServicesOf(userinfo: CsUserinfo, organizationId: string): string[] {
    return userinfo.memberships
      .filter(m => m.organization.organization_urn === organizationId)
      .flatMap(m => m.organization.licenses.map(l => l.service));
  }
}
